import { useEnum } from "@/api/services/global/enum";
import { ConfirmDeleteModal } from "@/components/commons/ConfirmDeleteModal";
import { FormField } from "@/components/elements/FormField";
import { Input } from "@/components/elements/Input";
import { Select } from "@/components/elements/Select";
import { TTalentForm } from "@/features/talent/types/talentForm";
import { blue } from "@/theme/colors";
import { FCC } from "@/types/common";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import ClearIcon from "@mui/icons-material/Clear";
import { Grid, Stack, Typography } from "@mui/material";
import { useCallback, useState } from "react";
import { useFieldArray, useFormContext, useWatch } from "react-hook-form";

const CIRCLED_NUMBERS = ["①", "②", "③", "④"];
const MAX_CONTACT_METHODS = 4;

type TalentFormContactMethodsProps = {};

/**
 * Renders the その他の連絡手段・SNS / その他の連絡先ID等 field array.
 *
 * Extracted from TalentFormBasicInfo so the unified-scan 全項目 tab can reuse
 * the same UX (per IROHANA_MATCH_DEV-280 mockup) without duplicating the
 * useFieldArray + add/remove + dedupe-options logic.
 *
 * Owner of all internal state (delete-confirm modal, append/remove handlers,
 * dedupe of contactType options across rows). Consumers just drop it into
 * their form between any sibling fields.
 */
export const TalentFormContactMethods: FCC<TalentFormContactMethodsProps> = () => {
  const { control } = useFormContext<TTalentForm>();
  const { data: contactTypeOptions } = useEnum("ETalentContactType");

  const {
    fields: contactMethodFields,
    append: appendContactMethod,
    remove: removeContactMethod,
  } = useFieldArray({ control, name: "contactMethods" });

  const watchedContactMethods = useWatch({ control, name: "contactMethods" });

  const [deleteConfirmIndex, setDeleteConfirmIndex] = useState<number | null>(null);

  const handleRemoveContactMethod = useCallback(
    (index: number) => {
      const method = watchedContactMethods?.[index];
      if (method?.contactType != null || (method?.contactInfo != null && method.contactInfo !== "")) {
        setDeleteConfirmIndex(index);
      } else {
        removeContactMethod(index);
      }
    },
    [watchedContactMethods, removeContactMethod],
  );

  // Hide already-selected contact types from sibling rows so the user cannot
  // pick the same type twice.
  const getFilteredContactTypeOptions = useCallback(
    (currentIndex: number) => {
      const selectedTypes = (watchedContactMethods ?? [])
        .filter((_, index) => index !== currentIndex)
        .map((method) => method?.contactType)
        .filter((type): type is number => type != null);
      return (contactTypeOptions ?? []).filter((option) => !selectedTypes.includes(Number(option.value)));
    },
    [watchedContactMethods, contactTypeOptions],
  );

  return (
    <>
      {contactMethodFields.map((field, index) => (
        <Stack key={field.id} mt={2}>
          <Grid container spacing={3}>
            <Grid item xs={6}>
              <FormField
                control={control}
                name={`contactMethods.${index}.contactType`}
                label={`その他の連絡手段・SNS${CIRCLED_NUMBERS[index] ?? index + 1}`}
              >
                <Select placeholder="選択してください" options={getFilteredContactTypeOptions(index)} />
              </FormField>
            </Grid>
            <Grid item xs={6}>
              <FormField
                control={control}
                name={`contactMethods.${index}.contactInfo`}
                label={`その他の連絡先ID等${CIRCLED_NUMBERS[index] ?? index + 1}`}
              >
                <Input placeholder="入力してください" />
              </FormField>
            </Grid>
          </Grid>
          <Stack direction="row" alignItems="center" spacing={2} mt={1}>
            {index === contactMethodFields.length - 1 && contactMethodFields.length < MAX_CONTACT_METHODS && (
              <Stack
                direction="row"
                alignItems="center"
                spacing={0.5}
                sx={{ cursor: "pointer" }}
                onClick={() => appendContactMethod({ contactType: null, contactInfo: null })}
              >
                <AddCircleOutlineIcon sx={{ color: blue[80], fontSize: 18 }} />
                <Typography variant="cap12">その他の連絡手段・SNS、連絡先ID等を追加</Typography>
              </Stack>
            )}
            {contactMethodFields.length > 1 && (
              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ cursor: "pointer" }} onClick={() => handleRemoveContactMethod(index)}>
                <ClearIcon sx={{ color: blue[80], fontSize: 18, display: "flex" }} />
                <Typography variant="cap12">削除</Typography>
              </Stack>
            )}
          </Stack>
        </Stack>
      ))}

      <ConfirmDeleteModal
        open={deleteConfirmIndex !== null}
        onClose={() => setDeleteConfirmIndex(null)}
        onOk={() => {
          if (deleteConfirmIndex !== null) {
            removeContactMethod(deleteConfirmIndex);
            setDeleteConfirmIndex(null);
          }
        }}
        message="入力されたデータが削除されます。よろしいですか？"
      />
    </>
  );
};
